/**
 * Entry Engagement Manager
 * Manages users who have signed up but haven't created their first journal entry
 * Allows admin to view, filter, and send nudge emails to encourage first entries
 */
import { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { 
  BookOpen, Users, Mail, Search, RefreshCw, AlertCircle, Check,
  ChevronDown, Download, User, Calendar, Clock, Send, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_BASE = '';

const EntryEngagementManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, not_nudged, nudged
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/users-without-entries`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
        withCredentials: true
      });
      
      setUsers(res.data.users || []);
      setStats({
        total_without_entries: res.data.total_without_entries,
        total_with_entries: res.data.total_with_entries,
        total_users: res.data.total_users,
        activation_rate: res.data.activation_rate,
        nudge_sent: res.data.stats?.nudge_sent || 0,
        not_nudged: res.data.stats?.not_nudged || 0
      });
    } catch (err) {
      console.error('Failed to fetch users without entries:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const getFilteredUsers = () => {
    let filtered = users;
    
    // Apply filter
    if (filterType === 'not_nudged') {
      filtered = filtered.filter(u => !u.first_entry_nudge_sent);
    } else if (filterType === 'nudged') {
      filtered = filtered.filter(u => u.first_entry_nudge_sent);
    }
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(query) ||
        u.first_name?.toLowerCase().includes(query) ||
        u.last_name?.toLowerCase().includes(query) ||
        u.name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const handleSelectAll = () => {
    const filtered = getFilteredUsers();
    if (selectedUsers.length === filtered.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filtered.map(u => u.user_id));
    }
  };

  const handleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleSendNudge = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to send nudge emails');
      return;
    }
    
    try {
      setSending(true);
      const res = await axios.post(`${API_BASE}/api/admin/users-without-entries/send-nudge`, {
        user_ids: selectedUsers
      }, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') },
        withCredentials: true
      });
      
      toast.success(`Sent ${res.data.sent} nudge emails!`);
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} emails failed to send`);
      }
      
      setSelectedUsers([]);
      fetchUsers();
    } catch (err) {
      console.error('Failed to send nudge emails:', err);
      toast.error('Failed to send nudge emails');
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = getFilteredUsers();

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                <p className="text-sm text-[#FDF2F8]/50">Total Users</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{stats.total_with_entries}</p>
                <p className="text-sm text-[#FDF2F8]/50">Have Entries</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-orange-500/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-400">{stats.total_without_entries}</p>
                <p className="text-sm text-[#FDF2F8]/50">No Entries Yet</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#df1283]/30 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#df1283]/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-[#df1283]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#df1283]">{stats.activation_rate}%</p>
                <p className="text-sm text-[#FDF2F8]/50">Activation Rate</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nudge Stats */}
      {stats && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
            <p className="text-sm text-[#FDF2F8]/60 mb-1">Already Nudged</p>
            <p className="text-xl font-bold text-[#0FFFE2]">{stats.nudge_sent}</p>
            <p className="text-xs text-[#FDF2F8]/40 mt-1">Users received nudge email</p>
          </div>
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
            <p className="text-sm text-[#FDF2F8]/60 mb-1">Not Yet Nudged</p>
            <p className="text-xl font-bold text-[#F59E0B]">{stats.not_nudged}</p>
            <p className="text-xs text-[#FDF2F8]/40 mt-1">Ready for outreach</p>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/40" />
              <input
                type="text"
                placeholder="Search by email or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#4A0A66]/30 border border-[#4A0A66] rounded-lg text-white text-sm placeholder:text-[#FDF2F8]/40 focus:outline-none focus:border-[#df1283]"
              />
            </div>
            
            <Button
              onClick={fetchUsers}
              variant="ghost"
              size="sm"
              className="text-[#FDF2F8]/60 hover:text-white"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Filter buttons */}
            {[
              { key: 'all', label: 'All' },
              { key: 'not_nudged', label: 'Not Nudged' },
              { key: 'nudged', label: 'Already Nudged' }
            ].map(filter => (
              <button
                key={filter.key}
                onClick={() => setFilterType(filter.key)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filterType === filter.key
                    ? 'bg-[#df1283] text-white'
                    : 'bg-[#4A0A66]/30 text-[#FDF2F8]/70 hover:bg-[#4A0A66]/50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Selection and Send Nudge */}
        {filteredUsers.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#4A0A66] flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-[#4A0A66] bg-[#4A0A66]/30 text-[#df1283] focus:ring-[#df1283]"
                />
                <span className="text-sm text-[#FDF2F8]/70">
                  Select All ({filteredUsers.length})
                </span>
              </label>
              {selectedUsers.length > 0 && (
                <span className="text-sm text-[#df1283]">
                  {selectedUsers.length} selected
                </span>
              )}
            </div>
            
            <Button
              onClick={handleSendNudge}
              disabled={selectedUsers.length === 0 || sending}
              className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90"
            >
              {sending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Nudge Email ({selectedUsers.length})
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* User List */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#4A0A66]">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-400" />
            Users Without Entries
          </h3>
          <p className="text-sm text-[#FDF2F8]/50 mt-1">
            These users signed up but haven't created their first journal entry yet
          </p>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-[#df1283] animate-spin mx-auto mb-2" />
            <p className="text-[#FDF2F8]/60">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-white font-medium">All users have created entries!</p>
            <p className="text-[#FDF2F8]/50 text-sm mt-1">Great job engaging your users 🎉</p>
          </div>
        ) : (
          <div className="divide-y divide-[#4A0A66]/50">
            {filteredUsers.map(user => (
              <div 
                key={user.user_id}
                className={`p-4 flex items-center gap-4 hover:bg-[#4A0A66]/20 transition-colors ${
                  selectedUsers.includes(user.user_id) ? 'bg-[#df1283]/10' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.user_id)}
                  onChange={() => handleSelectUser(user.user_id)}
                  className="w-4 h-4 rounded border-[#4A0A66] bg-[#4A0A66]/30 text-[#df1283] focus:ring-[#df1283]"
                />
                
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#df1283] to-[#FF7901] flex items-center justify-center overflow-hidden">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-medium">
                      {(user.first_name || user.name || user.email)?.[0]?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                
                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">
                    {user.first_name && user.last_name 
                      ? `${user.first_name} ${user.last_name}`
                      : user.name || 'No Name'}
                  </p>
                  <p className="text-sm text-[#FDF2F8]/50 truncate">{user.email}</p>
                </div>
                
                {/* Signup Date */}
                <div className="hidden md:block text-right">
                  <p className="text-xs text-[#FDF2F8]/40">Signed up</p>
                  <p className="text-sm text-white">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
                
                {/* Nudge Status */}
                <div className="text-right">
                  {user.first_entry_nudge_sent ? (
                    <div className="flex items-center gap-1 text-[#0FFFE2]">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-xs">Nudged</span>
                    </div>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs bg-orange-500/20 text-orange-400">
                      Not nudged
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EntryEngagementManager;
